import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  InputField,
  BasicSelectField,
  BasicFilterSelectField,
  RadioListField,
} from 'lib/iterable-hook-form';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useProjectTimeZones, useCreateProjectJson } from 'api/ProjectsAPI';
import {
  changeUserProject,
  selectIsBossOrSalesBossLevel,
  selectFeatureFlags,
} from 'core/userContext';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { FormField } from 'ui/formField';
import { BossBadge } from 'ui/badge';
import { Button } from 'ui/button';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'ui/modal';
import { OpenNewWindow16 } from 'ui/icons';
import { WebLink2 } from 'ui/theme';
import { useModalContext } from 'utils/contexts/modal';
import { NAME, TIME_ZONE, SHARD_COUNT } from '../constants';
import { createProjectPayload } from '../utils';
import { NewProjectFormProvider, NewProjectFormSchema } from '../contexts/NewProjectFormProvider';
import { shardCountOptions } from './CreateProjectModal.constants';
import { useUniqueIdentifierOptions } from '../hooks/useUniqueIdentifierOptions';

const TextWithMargin = styled.span`
  margin-right: 6px;
`;

const FlexWebLink2 = styled(WebLink2)`
  align-items: center;
  display: inline-flex;
  font-size: 12px;
  > *:first-child {
    margin-right: 4px;
  }
`;

const BaseCreateProjectModal = () => {
  const dispatch = useDispatch();
  const isBoss = useSelector(selectIsBossOrSalesBossLevel);
  const uniqueIdentifierOptions = useUniqueIdentifierOptions();

  const {
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useFormContext<NewProjectFormSchema>();

  const { hideModal } = useModalContext();
  const {
    data: { allowedTimeZones = [] } = {},
  } = useProjectTimeZones();
  const { mutate, isLoading } = useCreateProjectJson({
    onSuccess: (response: Response | undefined) => {
      const { projectId } = response as any;
      const name = getValues(NAME);
      dispatch(addPositiveToast(`Successfully created project ${name}.`));
      dispatch(changeUserProject(projectId));
      hideModal();
    },
    onError: () => {
      dispatch(addNegativeToast('Something went wrong. Please try again.'));
    },
  });

  const timeZoneOptions = (allowedTimeZones as string[]).map((tz) => ({
    label: tz,
    id: tz,
    value: tz,
  }));

  const onSubmit = (values: FieldValues) => {
    const payload = createProjectPayload(values);
    mutate(payload);
  };

  return (
    <Modal id="create-project-modal" withPortal isModalOpen={true} onClose={hideModal}>
      <ModalHeader>Create project</ModalHeader>
      <ModalBody>
        <FormField
          labelText="Name"
          inputId="project-name"
          errorText={errors[NAME]?.message}
          hasError={Boolean(errors[NAME])}
        >
          <InputField id="project-name" name={NAME} />
        </FormField>
        {Boolean(allowedTimeZones.length) && (
          <FormField labelText="Time zone" inputId="time-zone-select">
            <BasicFilterSelectField
              id="time-zone-select"
              name={TIME_ZONE}
              options={timeZoneOptions}
              inputContent={{
                triggerPlaceholder: 'Select a timezone...',
                filterPlaceholder: 'Select a timezone...',
              }}
            />
          </FormField>
        )}

        <FormField
          labelText="Unique identifier"
          descriptionText={
            <p>
              Select the field(s) you’ll use to identify users. This impacts some features and APIs.
              Note, this can’t be changed later.&nbsp;
              <FlexWebLink2
                href="https://support.iterable.com/hc/articles/9216719179796"
                target="_blank"
              >
                <span>Learn more in our knowledge center.</span>
                <OpenNewWindow16 isColorInherited={true} />
              </FlexWebLink2>
            </p>
          }
        >
          <RadioListField name="uniqueIdentifier" options={uniqueIdentifierOptions} />
        </FormField>
        {isBoss && (
          <FormField
            inputId="shard-count-select"
            labelText={
              <>
                <TextWithMargin>
                  Estimated project size (combined user and event count)
                </TextWithMargin>
                <BossBadge />
              </>
            }
            descriptionText="We’ll allocate database resources based on this estimated project size."
          >
            <BasicSelectField
              id="shard-count-select"
              name={SHARD_COUNT}
              options={shardCountOptions}
            />
          </FormField>
        )}
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <Button
          id="create-project-modal-submit-btn"
          onClick={handleSubmit(onSubmit)}
          isDisabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          Create
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const CreateProjectModal = () => {
  const { EmailProjectTypePhaseout } = useSelector(selectFeatureFlags);

  return (
    <NewProjectFormProvider EmailProjectTypePhaseout={EmailProjectTypePhaseout}>
      <BaseCreateProjectModal />
    </NewProjectFormProvider>
  );
};
